import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import Login from './pages/Login';
import Register from './pages/Register';

// Lazy-load all page bundles so a broken admin page can't white-screen the whole app
const AffiliateLayout = lazy(() => import('./pages/affiliate/Layout'));
const AffiliateDashboard = lazy(() => import('./pages/affiliate/Dashboard'));
const AffiliateCampaigns = lazy(() => import('./pages/affiliate/Campaigns'));
const AffiliateCampaignDetail = lazy(() => import('./pages/affiliate/CampaignDetail'));
const AffiliateCommissions = lazy(() => import('./pages/affiliate/Commissions'));
const AffiliatePayouts = lazy(() => import('./pages/affiliate/Payouts'));
const AffiliateProfile = lazy(() => import('./pages/affiliate/Profile'));
const AffiliateCreatives = lazy(() => import('./pages/affiliate/Creatives'));
const AffiliateHelp = lazy(() => import('./pages/affiliate/Help'));
const AffiliateNotifications = lazy(() => import('./pages/affiliate/Notifications'));
const StripeConnectCallback = lazy(() => import('./pages/affiliate/StripeConnectCallback'));
const StripeConnectRefresh = lazy(() => import('./pages/affiliate/StripeConnectRefresh'));
const AffiliateCoupons = lazy(() => import('./pages/affiliate/Coupons'));

const AdminLayout = lazy(() => import('./pages/admin/Layout'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminAffiliates = lazy(() => import('./pages/admin/Affiliates'));
const AdminAffiliateDetail = lazy(() => import('./pages/admin/AffiliateDetail'));
const AdminCampaigns = lazy(() => import('./pages/admin/Campaigns'));
const AdminCampaignDetail = lazy(() => import('./pages/admin/CampaignDetail'));
const AdminConversions = lazy(() => import('./pages/admin/Conversions'));
const AdminConversionDetail = lazy(() => import('./pages/admin/ConversionDetail'));
const AdminCommissions = lazy(() => import('./pages/admin/Commissions'));
const AdminPayouts = lazy(() => import('./pages/admin/Payouts'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminPaymentProviders = lazy(() => import('./pages/admin/PaymentProviders'));
const AdminCouponManager = lazy(() => import('./pages/admin/CouponManager'));
const AdminCouponDetail = lazy(() => import('./pages/admin/CouponDetail'));

function ProtectedRoute({
  children,
  role,
}: {
  children: React.ReactNode;
  role: 'AFFILIATE' | 'ADMIN';
}) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (role === 'ADMIN' && user?.role !== 'ADMIN') {
    return <Navigate to="/affiliate/dashboard" replace />;
  }

  if (role === 'AFFILIATE' && user?.role === 'ADMIN') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}

const Loading = () => <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          <Route
            path="/affiliate"
            element={
              <ProtectedRoute role="AFFILIATE">
                <AffiliateLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AffiliateDashboard />} />
            <Route path="campaigns" element={<AffiliateCampaigns />} />
            <Route path="campaigns/:id" element={<AffiliateCampaignDetail />} />
            <Route path="creatives" element={<AffiliateCreatives />} />
            <Route path="notifications" element={<AffiliateNotifications />} />
            <Route path="help" element={<AffiliateHelp />} />
            <Route path="commissions" element={<AffiliateCommissions />} />
            <Route path="payouts" element={<AffiliatePayouts />} />
            <Route path="profile" element={<AffiliateProfile />} />
            <Route path="settings/stripe-connect/callback" element={<StripeConnectCallback />} />
            <Route path="settings/stripe-connect/refresh" element={<StripeConnectRefresh />} />
            <Route path="coupons" element={<AffiliateCoupons />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute role="ADMIN">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="affiliates" element={<AdminAffiliates />} />
            <Route path="affiliates/:id" element={<AdminAffiliateDetail />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="campaigns/:id" element={<AdminCampaignDetail />} />
            <Route path="conversions" element={<AdminConversions />} />
            <Route path="conversions/:id" element={<AdminConversionDetail />} />
            <Route path="commissions" element={<AdminCommissions />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="payment-providers" element={<AdminPaymentProviders />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="coupons" element={<AdminCouponManager />} />
            <Route path="coupons/:id" element={<AdminCouponDetail />} />
          </Route>

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App crash:', error.message, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 20, fontFamily: 'monospace', color: '#fff', background: '#900' }}>
          <h1>Runtime Error</h1>
          <pre>{this.state.error.message}</pre>
          <pre>{this.state.error.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}
